/**
 * CME E-mini / Micro E-mini Nasdaq-100 (NQ, MNQ) quarterly contract months: Mar, Jun, Sep, Dec.
 * Last trading day (per CME contract specs): trading terminates 9:30 a.m. ET on the 3rd Friday
 * of the contract month. We approximate LTD as the calendar date of that Friday.
 *
 * "Roll week" = the Sunday–Saturday calendar week containing that LTD (local timezone).
 * Used only for MNQ/NQ display — not ES.
 *
 * @see https://www.cmegroup.com/markets/equities/nasdaq/micro-e-mini-nasdaq-100.contractSpecs.html
 */

/** 0-based month indexes: March, June, September, December */
export const CME_NASDAQ_QUARTERLY_MONTHS = [2, 5, 8, 11];

/**
 * Day-of-month (1–31) for the 3rd Friday of the given year/month.
 * @param {number} year
 * @param {number} monthIndex0 0 = January
 */
export function getThirdFridayOfMonth(year, monthIndex0) {
  const first = new Date(year, monthIndex0, 1);
  const dow = first.getDay();
  const daysToFirstFriday = (5 - dow + 7) % 7;
  const firstFriday = 1 + daysToFirstFriday;
  return firstFriday + 14;
}

/** @returns {Date} local midnight on the 3rd Friday */
export function getNasdaqQuarterlyLastTradingDayDate(year, monthIndex0) {
  const d = getThirdFridayOfMonth(year, monthIndex0);
  return new Date(year, monthIndex0, d);
}

/**
 * Stable string for the Sunday-starting week containing a local calendar date.
 * @param {number} year
 * @param {number} monthIndex0
 * @param {number} day
 */
export function weekKeySundayStart(year, monthIndex0, day) {
  const d = new Date(year, monthIndex0, day);
  const s = new Date(d.getFullYear(), d.getMonth(), d.getDate());
  s.setDate(s.getDate() - s.getDay());
  s.setHours(0, 0, 0, 0);
  return `${s.getFullYear()}-${String(s.getMonth() + 1).padStart(2, '0')}-${String(s.getDate()).padStart(2, '0')}`;
}

/**
 * Whether MNQ/NQ roll-week highlighting applies for this contract type.
 */
export function isNasdaqFuturesContractType(contractType) {
  return contractType === 'MNQ' || contractType === 'NQ';
}

/**
 * If this local calendar day falls in the week containing CME LTD for any quarterly month
 * in the same calendar year, return details; otherwise inRollWeek: false.
 * @param {number} year
 * @param {number} monthIndex0
 * @param {number} day
 * @returns {{ inRollWeek: boolean, lastTradingDay: Date | null, quarterMonthIndex0: number | null }}
 */
export function getNasdaqRollWeekInfoForDay(year, monthIndex0, day) {
  const key = weekKeySundayStart(year, monthIndex0, day);
  for (const m of CME_NASDAQ_QUARTERLY_MONTHS) {
    const ltdD = getThirdFridayOfMonth(year, m);
    const ltd = new Date(year, m, ltdD);
    const wk = weekKeySundayStart(ltd.getFullYear(), ltd.getMonth(), ltd.getDate());
    if (wk === key) {
      return { inRollWeek: true, lastTradingDay: ltd, quarterMonthIndex0: m };
    }
  }
  return { inRollWeek: false, lastTradingDay: null, quarterMonthIndex0: null };
}

/**
 * Year grid: month index 0–11 has at least one day in a roll week in that year
 * (for NQ/MNQ, roll week for each quarter falls entirely inside that quarter month).
 */
export function isNasdaqRollQuarterMonth(year, monthIndex0) {
  return CME_NASDAQ_QUARTERLY_MONTHS.includes(monthIndex0);
}

/**
 * Strategy sheet row weeks use Monday as `mm.dd.yyyy`. True if any Mon–Fri overlaps a MNQ/NQ roll week.
 * @param {string} mondayDateStr
 */
export function nasdaqRollWeekTouchesWeekMonday(mondayDateStr) {
  const parts = (mondayDateStr || '').split('.');
  if (parts.length !== 3) return false;
  const mm = parseInt(parts[0], 10);
  const dd = parseInt(parts[1], 10);
  const yyyy = parseInt(parts[2], 10);
  if (isNaN(mm) || isNaN(dd) || isNaN(yyyy)) return false;
  for (let i = 0; i < 5; i++) {
    const d = new Date(yyyy, mm - 1, dd + i);
    if (getNasdaqRollWeekInfoForDay(d.getFullYear(), d.getMonth(), d.getDate()).inRollWeek) {
      return true;
    }
  }
  return false;
}
